import { useTranslation } from "react-i18next";

import type { TournamentNotificationSchema } from "@/services/tournament-notifications";
import {
  isNewHighScoreNotification,
  isTournamentPrizeNotification,
} from "@/services/tournament-notifications";
import { Notification } from "./notification";
import { useToastCampaign } from "./use-toast-campaign";

/**
 * Copy for one tournament notification. A prize is the whole message, as on the mission toast:
 * "You won · <prize>" on the gold card, with the final place under it. Without one, the event
 * leads and the place follows.
 */
function useCopy(notification: TournamentNotificationSchema): {
  title: string;
  label?: string;
  text?: string;
  variant: "default" | "prize";
} {
  const { t } = useTranslation();
  const finalPlace = t("tournament.finalPosition", {
    place: notification.player_place,
  });
  const currentPlace = t("tournament.yourPosition", {
    place: notification.player_place,
  });

  if (isTournamentPrizeNotification(notification) && notification.prize_title) {
    return {
      // Localized + currency-formatted server-side, so it goes out verbatim.
      title: notification.prize_title,
      label: t("prizes.youWon"),
      text: finalPlace,
      variant: "prize",
    };
  }
  if (isNewHighScoreNotification(notification)) {
    return {
      // A highest-win score is a multiplier, written as the standing card writes it.
      title: t("tournament.newHighScore", {
        score: `${notification.new_high_score.toLocaleString()}x`,
      }),
      text: currentPlace,
      variant: "default",
    };
  }
  switch (notification.type) {
    case "tournament_race_completed":
      return {
        title: t("campaign.raceCompleted"),
        text: currentPlace,
        variant: "default",
      };
    case "tournament_race_finished":
    case "tournament_secondary_race_finished":
      return {
        title: t("campaign.raceFinished"),
        text: finalPlace,
        variant: "default",
      };
    default:
      return {
        title: t("campaign.tournamentFinished"),
        text: finalPlace,
        variant: "default",
      };
  }
}

/** A tournament notification as a toast. Tapping it opens the campaign (`useToastCampaign`). */
export const TournamentToast = ({
  notification,
  onDismiss,
}: {
  notification: TournamentNotificationSchema;
  onDismiss: () => void;
}) => {
  const copy = useCopy(notification);
  const { imageUrl, onClick } = useToastCampaign(
    notification.campaign_id,
    onDismiss
  );

  return (
    <Notification
      title={copy.title}
      label={copy.label}
      text={copy.text}
      imageUrl={imageUrl}
      variant={copy.variant}
      onClick={onClick}
    />
  );
};
